import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SidePanel } from '../components/SidePanel';
import { DealFormModal } from '../components/DealFormModal';
import { DealDetail } from './DealDetailPage';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ExternalLink, Loader2, Plus } from 'lucide-react';
import { companyApi } from '../services/companyApi';
import { dealApi } from '../services/dealApi';
import { vehicleApi } from '../services/vehicleApi';
import { cashflowApi } from '../services/cashflowApi';
import {
  COMPANY_STATUSES, COMPANY_STATUS_LABEL, COMPANY_STATUS_STYLE, Company, CompanyStatus,
} from '../types/company';
import { Deal, SOURCE_LABEL, STAGE_STYLE, Valuation } from '../types/deal';
import { Cashflow } from '../types/cashflow';
import { KIND_STYLE, Vehicle, shortWon, won, KIND_LABEL } from '../types/vehicle';
import { mult, pct, xirr } from '../lib/metrics';
import { Round, rounds as roundApi } from '../services/companyDetailApi';
import { MGMT_LEVEL } from '../services/codes';
import { Tour, TourButton, TourStep } from '../components/Tour';
import { CompanyTabs } from '../components/CompanyTabs';

/**
 * 투자처 상세 — 「이 회사에 우리가 총 얼마」.
 *
 * ⚠️ 합계는 보여주되 **자금원별 줄을 지우지 않는다.** 고유자금과 위탁자금이 한 회사에
 *    같이 들어갔을 수 있다. 합계만 보면 누구 몫인지 사라진다.
 */
/** 주소로 들어올 때(/companies/:id). 투자처 목록에서는 오른쪽 패널로 연다 */
export const CompanyDetailPage: React.FC = () => {
  const { id } = useParams();
  return <CompanyDetail companyId={Number(id)} />;
};

/**
 * 투자처 상세 본문 — 화면 전체 · 오른쪽 패널 둘 다. 2026-11-02 「투자 건」 메뉴를 여기로 합쳤다 —
 * 자금원별 건을 여기서 추가하고, 줄을 누르면 투자 건 상세가 패널 위에 열린다.
 */
export const CompanyDetail: React.FC<{ companyId: number; inPanel?: boolean }> = ({ companyId, inPanel = false }) => {
  const navigate = useNavigate();
  const [allCompanies, setAllCompanies] = useState<Company[]>([]);
  const [dealOpen, setDealOpen] = useState(false);
  const [selDeal, setSelDeal] = useState<number | null>(null);

  const [company, setCompany] = useState<Company | null>(null);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [flows, setFlows] = useState<Cashflow[]>([]);
  const [vals, setVals] = useState<Valuation[]>([]);
  const [rounds, setRounds] = useState<Round[]>([]);
  const [loading, setLoading] = useState(true);
  const [tourRun, setTourRun] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, ds, vs, fs, all, rs] = await Promise.all([
        companyApi.get(companyId), dealApi.list(), vehicleApi.list(), cashflowApi.list(), dealApi.valuations(), roundApi.list({ companyId }),
      ]);
      const mine = ds.filter((d) => d.companyId === companyId);
      const ids = new Set(mine.map((d) => d.id));
      setCompany(c ?? null);
      if (c) setAllCompanies([c]);
      setDeals(mine);
      setVehicles(vs);
      setFlows(fs.filter((f) => f.dealId !== null && ids.has(f.dealId)));
      setVals(all.filter((v) => ids.has(v.dealId)).sort((a, b) => b.valuedOn.localeCompare(a.valuedOn)));
      setRounds(rs.filter((r) => r.postMoney).sort((a, b) => a.closedOn.localeCompare(b.closedOn)));
    } finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const vehicle = (vid: number) => vehicles.find((v) => v.id === vid);

  const rows = useMemo(() => deals.map((d) => {
    const mine = flows.filter((f) => f.dealId === d.id);
    const invested = mine.filter((f) => f.kind === 'investment').reduce((s, f) => s + f.amount, 0);
    const returned = mine.filter((f) => f.kind === 'exit' || f.kind === 'dividend').reduce((s, f) => s + f.amount, 0);
    const latest = vals.find((v) => v.dealId === d.id);
    const closed = d.stage === '회수' || d.stage === '상각';
    const res = closed ? 0 : (latest?.ourValue ?? 0);
    // 평가액 없는 보유 건은 배수를 「—」로 — 0 으로 세면 0.00x(전액 손실)처럼 보인다
    const unvalued = !closed && !latest;
    return { d, invested, returned, res, unvalued, moic: invested > 0 && !unvalued ? (returned + res) / invested : null };
  }), [deals, flows, vals]);

  const total = rows.reduce(
    (s, r) => ({ invested: s.invested + r.invested, returned: s.returned + r.returned, res: s.res + r.res }),
    { invested: 0, returned: 0, res: 0 },
  );
  const anyUnvalued = rows.some((r) => r.unvalued && r.invested > 0);
  const totalMoic = total.invested > 0 && !anyUnvalued ? (total.returned + total.res) / total.invested : null;

  // factsheet 포트폴리오 개요 — 최초 투자 기업가치 · 최근 기업가치 · Gross IRR(보수 떼기 전)
  const firstValue = rounds.find((r) => r.dealId)?.postMoney ?? null;
  const latestValue = [...rounds].reverse()[0]?.postMoney ?? vals.find((v) => v.companyValue)?.companyValue ?? null;
  const series: Array<[Date, number]> = flows
    .filter((f) => f.kind === 'investment' || f.kind === 'exit' || f.kind === 'dividend')
    .map((f) => [new Date(f.occurredOn), f.kind === 'investment' ? -f.amount : f.amount]);
  if (total.res > 0) series.push([new Date(), total.res]);
  series.sort((a, b) => a[0].getTime() - b[0].getTime());
  const grossIrr = anyUnvalued ? null : xirr(series);

  const setStatus = async (status: CompanyStatus) => {
    if (!company) return;
    await companyApi.update(company.id, { status });
    load();
  };

  const TOUR: TourStep[] = useMemo(() => [
    {
      name: 'head',
      target: '[data-tour="head"]',
      title: '회사 정보와 상태',
      content: '보유 중 · 회수 완료 · 상각 중 하나입니다. 모든 건이 끝나면 여기서 상태를 바꿉니다.',
    },
    {
      name: 'total',
      target: '[data-tour="total"]',
      title: '이 회사에 총 얼마',
      content: '모든 자금원의 건을 더한 값입니다. 누구 몫인지는 아래 표에서 자금원별로 봅니다.',
    },
    {
      name: 'deals',
      target: '[data-tour="deals"]',
      title: '자금원별 건',
      content: '같은 회사라도 통장이 다르면 건이 따로입니다. 누르면 건 상세로 갑니다.',
    },
    {
      name: 'vals',
      target: '[data-tour="vals"]',
      title: '평가 이력',
      content: '이 회사에 매긴 평가액을 시간순으로 모았습니다. 회사 전체 가치가 있으면 같이 보입니다.',
    },
    {
      name: 'tabs',
      target: '[data-tour="tabs"]',
      title: '기업정보 · 투자정보 · 재무 · 문서 · 타임라인',
      content: 'VICS 표시가 붙은 칸은 VICS 보고에 들어갑니다. 비어 있으면 VICS 보고 화면에 빈칸으로 뜹니다. 표의 줄에 마우스를 올리면 고치기 · 지우기가 나옵니다(지우기는 두 번). 재무제표는 문서 보관함에 올린 뒤 문서함에서 AI 로 읽고 검수하면 재무에 들어갑니다. 정기보고 · 리스크평가는 각 화면에서 씁니다.',
      placement: 'top',
    },
  ], []);

  if (loading && !company) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-sm text-slate-500">
        <Loader2 size={16} className="animate-spin" /> 불러오는 중...
      </div>
    );
  }
  if (!company) {
    return <div className="p-6 lg:p-8"><p className="text-sm text-slate-500">투자처를 찾을 수 없습니다.</p></div>;
  }

  return (
    <div className={inPanel ? '' : 'p-6 lg:p-8 max-w-5xl'}>
      {!inPanel && (
        <button onClick={() => navigate('/companies')}
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-2">
          <ChevronLeft size={14} /> 투자처
        </button>
      )}

      <div data-tour="head" className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-1.5">
            {company.name}
            <TourButton onClick={() => setTourRun(true)} />
          </h1>
          <p className="mt-1 text-xs text-slate-500 flex flex-wrap items-center gap-x-2 gap-y-0.5">
            {company.sector && <span>{company.sector}</span>}
            {company.foundedOn && <span>{company.foundedOn.replace(/-/g, '.')} 설립</span>}
            {company.bizNo && <span>{company.bizNo}</span>}
            {company.homepage && (
              <a href={company.homepage} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-indigo-600 hover:underline">
                홈페이지 <ExternalLink size={10} />
              </a>
            )}
          </p>
          {company.note && <p className="mt-1 text-xs text-slate-400 max-w-xl">{company.note}</p>}
          {company.mgmtLevel && (
            <div className="mt-1.5 flex items-center gap-1.5 text-xs">
              <span className={`rounded-full px-2 py-0.5 font-medium ${
                company.mgmtLevel === 'focus' ? 'bg-amber-50 text-amber-700' : company.mgmtLevel === 'closed' ? 'bg-slate-100 text-slate-500' : 'bg-emerald-50 text-emerald-700'
              }`}>{MGMT_LEVEL.toUi(company.mgmtLevel)}</span>
            </div>
          )}
        </div>
        <select aria-label="상태" value={company.status} onChange={(e) => setStatus(e.target.value as CompanyStatus)}
          className={`flex-shrink-0 rounded-full px-2.5 py-1 text-xs font-medium border-0 focus:ring-2 focus:ring-indigo-300 ${COMPANY_STATUS_STYLE[company.status]}`}>
          {COMPANY_STATUSES.map((s) => <option key={s} value={s}>{COMPANY_STATUS_LABEL[s]}</option>)}
        </select>
      </div>

      <div data-tour="total" className={`grid grid-cols-2 sm:grid-cols-4 ${inPanel ? '' : 'lg:grid-cols-7'} gap-2 mb-5`}>
        {[
          ['최초 투자 기업가치', firstValue ? shortWon(firstValue) : '—'],
          ['최근 기업가치', latestValue ? shortWon(latestValue) : '—'],
          ['투자', shortWon(total.invested)],
          ['회수', total.returned > 0 ? shortWon(total.returned) : '—'],
          ['평가', total.res > 0 ? shortWon(total.res) : '—'],
          ['MOIC', mult(totalMoic)],
          ['Gross IRR', pct(grossIrr)],
        ].map(([k, v]) => (
          <div key={k} className="bg-white border border-slate-200 rounded-lg px-3.5 py-2.5">
            <div className="text-xs text-slate-400">{k}</div>
            <div className="mt-0.5 text-base font-bold text-slate-900 tabular-nums">{v}</div>
          </div>
        ))}
      </div>

      <section data-tour="deals" className="bg-white border border-slate-200 rounded-lg mb-5">
        <h2 className="flex items-center justify-between text-sm font-semibold text-slate-900 px-4 py-3 border-b border-slate-100">
          <span>투자 건 <span className="font-normal text-slate-400">— 자금원별 {deals.length}</span></span>
          <button type="button" onClick={() => setDealOpen(true)}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border border-slate-200 text-slate-700 hover:bg-slate-50">
            <Plus size={12} /> 투자 건 추가
          </button>
        </h2>
        {rows.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-500">아직 투자 건이 없습니다. 「투자 건 추가」로 자금원별로 만듭니다.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-xs text-slate-400">
                  <th className="text-left font-medium px-4 py-2">자금원</th>
                  <th className="text-left font-medium px-2 py-2">증권</th>
                  <th className="text-right font-medium px-2 py-2">투자</th>
                  <th className="text-right font-medium px-2 py-2">회수</th>
                  <th className="text-right font-medium px-2 py-2">평가</th>
                  <th className="text-right font-medium px-2 py-2">배수</th>
                  <th className="text-left font-medium px-4 py-2">단계</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ d, invested, returned, res, moic }) => {
                  const v = vehicle(d.vehicleId);
                  return (
                    <tr key={d.id} onClick={() => setSelDeal(d.id)} className="border-b border-slate-50 last:border-b-0 cursor-pointer hover:bg-slate-50">
                      <td className="px-4 py-2.5">
                        <button type="button" className="font-medium text-slate-900 hover:text-indigo-600 text-left">{v?.name ?? '—'}</button>
                        {v && <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-xs ${KIND_STYLE[v.kind]}`}>{KIND_LABEL[v.kind]}</span>}
                      </td>
                      <td className="px-2 py-2.5 text-xs text-slate-600">{d.securityKind ?? '—'}</td>
                      <td className="px-2 py-2.5 text-right tabular-nums text-slate-900 whitespace-nowrap">{shortWon(invested)}</td>
                      <td className="px-2 py-2.5 text-right tabular-nums text-slate-700 whitespace-nowrap">{returned > 0 ? shortWon(returned) : '—'}</td>
                      <td className="px-2 py-2.5 text-right tabular-nums text-slate-700 whitespace-nowrap">{res > 0 ? shortWon(res) : '—'}</td>
                      <td className={`px-2 py-2.5 text-right tabular-nums font-semibold whitespace-nowrap ${
                        moic === null ? 'text-slate-400' : moic >= 1 ? 'text-emerald-700' : 'text-red-600'
                      }`}>{mult(moic)}</td>
                      <td className="px-4 py-2.5"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STAGE_STYLE[d.stage]}`}>{d.stage}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section data-tour="vals" className="bg-white border border-slate-200 rounded-lg">
        <h2 className="text-sm font-semibold text-slate-900 px-4 py-3 border-b border-slate-100">평가 이력</h2>
        {vals.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-500">아직 매긴 값이 없습니다. 건 상세에서 추가합니다.</p>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {vals.map((v) => (
                <tr key={v.id} className="border-b border-slate-50 last:border-b-0">
                  <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap tabular-nums">{v.valuedOn.replace(/-/g, '.')}</td>
                  <td className="px-2 py-2.5 text-xs text-slate-600">{vehicle(deals.find((d) => d.id === v.dealId)?.vehicleId ?? -1)?.name ?? '—'}</td>
                  <td className="px-2 py-2.5 text-xs text-slate-500">{SOURCE_LABEL[v.source]}</td>
                  <td className="px-2 py-2.5 text-right text-xs text-slate-400 tabular-nums whitespace-nowrap">
                    {v.companyValue ? `회사 ${shortWon(v.companyValue)}` : ''}
                  </td>
                  <td className="px-4 py-2.5 text-right font-medium text-slate-900 whitespace-nowrap tabular-nums">{won(v.ourValue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <CompanyTabs company={company} deals={deals} flows={flows} vehicles={vehicles} onCompanyChange={load} />
      <DealFormModal open={dealOpen} vehicles={vehicles} companies={allCompanies} companyId={companyId}
        onClose={() => setDealOpen(false)} onSaved={() => { setDealOpen(false); load(); }} />
      {selDeal !== null && (
        <SidePanel onClose={() => { setSelDeal(null); load(); }}
          header={<div className="flex items-center gap-2 text-xs text-slate-500">투자 건 상세
            <Link to={`/deals/${selDeal}`} className="inline-flex items-center gap-0.5 text-indigo-600 hover:underline">전체 화면 <ExternalLink size={11} /></Link></div>}>
          <DealDetail key={selDeal} dealId={selDeal} inPanel />
        </SidePanel>
      )}

      <Tour steps={TOUR} run={tourRun} onFinish={() => setTourRun(false)} />
    </div>
  );
};
